import { InboxIcon, SearchXIcon } from 'lucide-react';
import type * as React from 'react';

import { useTranslation } from '@nucleus-commerce/i18n/react';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { TableCell, TableRow } from '@nucleus-commerce/ui/components/table';

import { ErrorState } from '../errors/error-state';

export interface DataTableEmptyStateProps {
  colSpan: number;
  title?: string;
  description?: string;
  /** A search or filter produced no rows (as opposed to there being no data). */
  filtered?: boolean;
  /** Rendered under the text: "Clear filters", "Add product". */
  action?: React.ReactNode;
}

function DataTableEmptyState({
  colSpan,
  title,
  description,
  filtered = false,
  action,
}: DataTableEmptyStateProps) {
  const { t } = useTranslation('common');
  const Icon = filtered ? SearchXIcon : InboxIcon;
  return (
    <TableRow className="hover:bg-transparent">
      <TableCell colSpan={colSpan} className="h-32 text-center whitespace-normal">
        <div className="text-muted-foreground flex flex-col items-center gap-1">
          <Icon aria-hidden className="size-6" />
          <span className="text-foreground font-medium">{title ?? t('indexTable.noResults')}</span>
          {description ? <span className="text-sm">{description}</span> : null}
          {action ? <div className="mt-2">{action}</div> : null}
        </div>
      </TableCell>
    </TableRow>
  );
}

export interface DataTableErrorStateProps {
  colSpan: number;
  error: unknown;
  onRetry?: (() => void) | undefined;
}

/** The query failed: the shared `ErrorState`, so the request id is always shown. */
function DataTableErrorState({ colSpan, error, onRetry }: DataTableErrorStateProps) {
  return (
    <TableRow className="hover:bg-transparent">
      <TableCell colSpan={colSpan} className="p-6 whitespace-normal">
        <ErrorState error={error} onRetry={onRetry} compact />
      </TableCell>
    </TableRow>
  );
}

export interface DataTableLoadingStateProps {
  colSpan: number;
  rows?: number;
  /** "Loading products". */
  label?: string;
}

function DataTableLoadingState({ colSpan, rows = 5, label }: DataTableLoadingStateProps) {
  const { t } = useTranslation('common');
  return (
    <>
      <TableRow className="sr-only hover:bg-transparent" aria-live="polite">
        <TableCell colSpan={colSpan} role="status">
          {label ?? t('indexTable.loadingRows')}
        </TableCell>
      </TableRow>
      {Array.from({ length: rows }).map((_, index) => (
        <TableRow key={index} aria-hidden className="hover:bg-transparent">
          <TableCell colSpan={colSpan}>
            <Skeleton className="h-4 w-full max-w-64" />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

export { DataTableEmptyState, DataTableErrorState, DataTableLoadingState };
